import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import Image from 'next/image'
import { useRef, useState } from 'react'

import {
    ArrowRight,
    CalendarCheck,
    ChevronDown,
    CircleCheck,
    CreditCard,
    House,
    LoaderCircle
} from 'lucide-react'
import { cn } from '@/lib/utils'
import Reveal from '@/components/generals/reveal'

import {
    EMAIL_PATTERN,
    HOME_TYPES,
    HOUSEHOLDS,
    REGIONS
} from '@/constants/reservation'

type Field = 'name' | 'email' | 'region' | 'homeType' | 'household'

type FormValues = Record<Field, string> & { company: string }

type Status = 'idle' | 'submitting' | 'success' | 'error'

const INITIAL: FormValues = {
    name: '',
    email: '',
    region: '',
    homeType: '',
    household: '',
    company: ''
}

const PROMISES = [
    { icon: CreditCard, text: "No payment today" },
    { icon: House, text: "A home fit visit before delivery" },
    { icon: CalendarCheck, text: "Cancel any time, in one email" }
]

const validate = (values: FormValues): Partial<Record<Field, string>> => {
    const errors: Partial<Record<Field, string>> = {}

    if (values.name.trim().length < 2) errors.name = 'Please enter your full name.'
    if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = 'Please enter a valid email address.'
    if (!values.region) errors.region = 'Please choose where you live.'
    if (!values.homeType) errors.homeType = 'Please choose your type of home.'

    return errors
}

const inputClass = 'h-12 w-full rounded-xl border border-input bg-background px-4 text-[15px] text-foreground transition-colors placeholder:text-muted-foreground/70 hover:border-foreground/30 focus:border-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 aria-[invalid=true]:border-destructive'

const Reserve: FC = (): ReactNode => {
    const [values, setValues] = useState<FormValues>(INITIAL)
    const [errors, setErrors] = useState<Partial<Record<Field, string>>>({})
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')
    const [updated, setUpdated] = useState(false)
    const [submitted, setSubmitted] = useState({ email: '', region: '' })
    const formRef = useRef<HTMLFormElement>(null)

    const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const value = event.target.value

        setValues(current => ({ ...current, [field]: value }))

        if (field in errors) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const found = validate(values)

        setErrors(found)

        const firstInvalid = (Object.keys(found) as Array<Field>)[0]

        if (firstInvalid) {
            formRef.current?.querySelector<HTMLElement>(`[name="${firstInvalid}"]`)?.focus()

            return
        }

        setStatus('submitting')
        setMessage('')

        try {
            const response = await fetch('/api/reserve', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const result = await response.json().catch(() => null) as { ok?: boolean, updated?: boolean, error?: string, field?: Field } | null

            if (!response.ok || !result?.ok) {
                if (result?.field) setErrors({ [result.field]: result.error })

                setStatus('error')
                setMessage(result?.error || "We couldn't save your reservation just now. Please try again in a moment.")

                return
            }

            setSubmitted({ email: values.email.trim(), region: values.region })
            setUpdated(Boolean(result.updated))
            setStatus('success')
            setValues(INITIAL)
        } catch {
            setStatus('error')
            setMessage("We couldn't reach our servers. Check your connection and try again.")
        }
    }

    const errorId = (field: Field) => errors[field] ? `${field}-error` : undefined

    return (
        <section id='reserve' aria-labelledby='reserve-title' className='relative isolate overflow-hidden bg-ink text-ink-foreground'>
            <div aria-hidden='true' className='absolute inset-0 -z-10'>
                <Image
                    src='/images/day-tidy.jpg'
                    alt=''
                    fill
                    sizes='100vw'
                    className='object-cover object-[60%_50%] opacity-45'
                />

                <div className='absolute inset-0 bg-[linear-gradient(to_right,oklch(0.165_0.01_55/0.96)_0%,oklch(0.165_0.01_55/0.82)_45%,oklch(0.165_0.01_55/0.55)_100%)]' />
                <div className='absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60' />
            </div>

            <div className='mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:px-8 md:py-28 lg:grid-cols-12 lg:gap-10'>
                <Reveal className='flex flex-col lg:col-span-6'>
                    <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-ember'>
                        Reservations
                    </p>

                    <h2 id='reserve-title' className='mt-5 font-heading text-[clamp(3rem,6.5vw,6rem)] leading-[0.92] tracking-[-0.02em]'>
                        Bring Oriel <em className='italic text-ember'>home.</em>
                    </h2>

                    <p className='mt-6 max-w-md text-lg leading-relaxed text-ink-foreground/75'>
                        First deliveries begin in spring 2027 in the United States and United Kingdom. Reserving is free and holds your place in line.
                    </p>

                    <ul className='mt-10 flex flex-col gap-4'>
                        {PROMISES.map(item => (
                            <li key={item.text} className='flex items-center gap-4 text-[15px] text-ink-foreground/85'>
                                <span className='flex size-10 items-center justify-center rounded-full border border-white/15 bg-white/5'>
                                    <item.icon className='size-4 text-ember' aria-hidden='true' />
                                </span>

                                {item.text}
                            </li>
                        ))}
                    </ul>
                </Reveal>

                <Reveal delay={150} className='lg:col-span-6 lg:pl-6'>
                    <div className='rounded-[2rem] bg-card p-6 text-card-foreground shadow-[0_40px_120px_-40px_oklch(0_0_0/0.8)] md:p-9'>
                        {status === 'success' ? (
                            <div role='status' className='flex min-h-[28rem] flex-col items-start justify-center'>
                                <span className='flex size-14 items-center justify-center rounded-full bg-foreground text-background'>
                                    <CircleCheck className='size-6' aria-hidden='true' />
                                </span>

                                <h3 className='mt-6 font-heading text-4xl leading-tight md:text-5xl'>
                                    {updated ? "Your details are updated." : "You're on the list."}
                                </h3>

                                <p className='mt-4 max-w-sm text-[15px] leading-relaxed text-muted-foreground'>
                                    We'll write to <span className='font-medium text-foreground'>{submitted.email}</span> before deliveries open in {submitted.region === 'Somewhere else' ? 'your area' : submitted.region}, and not before.
                                </p>

                                <button
                                    type='button'
                                    onClick={() => setStatus('idle')}
                                    className='mt-8 text-sm font-medium text-foreground underline underline-offset-4 transition-colors hover:text-ember-deep focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring'
                                >
                                    Reserve for someone else
                                </button>
                            </div>
                        ) : (
                            <form ref={formRef} onSubmit={onSubmit} noValidate className='relative flex flex-col gap-5'>
                                <div className='flex items-baseline justify-between gap-4'>
                                    <h3 className='font-heading text-3xl leading-tight'>
                                        Reserve your place
                                    </h3>

                                    <span className='font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground'>
                                        Takes a minute
                                    </span>
                                </div>

                                <div className='grid gap-5 sm:grid-cols-2'>
                                    <div className='flex flex-col gap-2'>
                                        <label htmlFor='name' className='text-sm font-medium'>
                                            Full name
                                        </label>

                                        <input
                                            id='name'
                                            name='name'
                                            type='text'
                                            autoComplete='name'
                                            value={values.name}
                                            onChange={update('name')}
                                            aria-invalid={Boolean(errors.name)}
                                            aria-describedby={errorId('name')}
                                            className={inputClass}
                                        />

                                        {errors.name && (
                                            <p id='name-error' className='text-sm text-destructive'>
                                                {errors.name}
                                            </p>
                                        )}
                                    </div>

                                    <div className='flex flex-col gap-2'>
                                        <label htmlFor='email' className='text-sm font-medium'>
                                            Email
                                        </label>

                                        <input
                                            id='email'
                                            name='email'
                                            type='email'
                                            inputMode='email'
                                            autoComplete='email'
                                            value={values.email}
                                            onChange={update('email')}
                                            aria-invalid={Boolean(errors.email)}
                                            aria-describedby={errorId('email')}
                                            className={inputClass}
                                        />

                                        {errors.email && (
                                            <p id='email-error' className='text-sm text-destructive'>
                                                {errors.email}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className='grid gap-5 sm:grid-cols-2'>
                                    <div className='flex flex-col gap-2'>
                                        <label htmlFor='region' className='text-sm font-medium'>
                                            Where you live
                                        </label>

                                        <div className='relative'>
                                            <select
                                                id='region'
                                                name='region'
                                                autoComplete='country-name'
                                                value={values.region}
                                                onChange={update('region')}
                                                aria-invalid={Boolean(errors.region)}
                                                aria-describedby={errorId('region')}
                                                className={cn(inputClass, 'appearance-none pr-10', !values.region && 'text-muted-foreground')}
                                            >
                                                <option value='' disabled>
                                                    Choose a region
                                                </option>

                                                {REGIONS.map(region => (
                                                    <option key={region} value={region} className='text-foreground'>
                                                        {region}
                                                    </option>
                                                ))}
                                            </select>

                                            <ChevronDown className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                                        </div>

                                        {errors.region && (
                                            <p id='region-error' className='text-sm text-destructive'>
                                                {errors.region}
                                            </p>
                                        )}
                                    </div>

                                    <div className='flex flex-col gap-2'>
                                        <label htmlFor='household' className='text-sm font-medium'>
                                            Who lives with you <span className='font-normal text-muted-foreground'>(optional)</span>
                                        </label>

                                        <div className='relative'>
                                            <select
                                                id='household'
                                                name='household'
                                                value={values.household}
                                                onChange={update('household')}
                                                aria-invalid={Boolean(errors.household)}
                                                aria-describedby={errorId('household')}
                                                className={cn(inputClass, 'appearance-none pr-10', !values.household && 'text-muted-foreground')}
                                            >
                                                <option value=''>
                                                    Prefer not to say
                                                </option>

                                                {HOUSEHOLDS.map(household => (
                                                    <option key={household} value={household} className='text-foreground'>
                                                        {household}
                                                    </option>
                                                ))}
                                            </select>

                                            <ChevronDown className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                                        </div>

                                        {errors.household && (
                                            <p id='household-error' className='text-sm text-destructive'>
                                                {errors.household}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <fieldset aria-describedby={errorId('homeType')} className='flex flex-col gap-2'>
                                    <legend className='mb-2 text-sm font-medium'>
                                        Your home
                                    </legend>

                                    <div className='grid gap-2 sm:grid-cols-3'>
                                        {HOME_TYPES.map(type => {
                                            const checked = values.homeType === type

                                            return (
                                                <label
                                                    key={type}
                                                    className={cn(
                                                        'flex h-12 cursor-pointer items-center justify-center rounded-xl border px-3 text-center text-sm transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/40',
                                                        checked
                                                            ? 'border-foreground bg-foreground text-background'
                                                            : errors.homeType
                                                                ? 'border-destructive bg-background text-foreground'
                                                                : 'border-input bg-background text-foreground hover:border-foreground/30'
                                                    )}
                                                >
                                                    <input
                                                        type='radio'
                                                        name='homeType'
                                                        value={type}
                                                        checked={checked}
                                                        onChange={update('homeType')}
                                                        className='sr-only'
                                                    />
                                                    {type}
                                                </label>
                                            )
                                        })}
                                    </div>

                                    {errors.homeType && (
                                        <p id='homeType-error' className='text-sm text-destructive'>
                                            {errors.homeType}
                                        </p>
                                    )}
                                </fieldset>

                                <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                                    <label htmlFor='company'>
                                        Company
                                    </label>

                                    <input
                                        id='company'
                                        name='company'
                                        type='text'
                                        tabIndex={-1}
                                        autoComplete='off'
                                        value={values.company}
                                        onChange={update('company')}
                                    />
                                </div>

                                {status === 'error' && message && (
                                    <p role='alert' className='rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
                                        {message}
                                    </p>
                                )}

                                <button
                                    type='submit'
                                    disabled={status === 'submitting'}
                                    className='group mt-1 inline-flex h-13 items-center justify-center gap-2 rounded-full bg-foreground px-6 text-[15px] font-medium text-background transition-colors hover:bg-ember-deep focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-wait disabled:opacity-70'
                                >
                                    {status === 'submitting' ? (
                                        <>
                                            <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                                            Saving your place
                                        </>
                                    ) : (
                                        <>
                                            Reserve Oriel
                                            <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-0.5' aria-hidden='true' />
                                        </>
                                    )}
                                </button>

                                <p className='text-center text-xs leading-relaxed text-muted-foreground'>
                                    We only use your details to arrange your reservation. No marketing lists.
                                </p>
                            </form>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    )
}

export default Reserve
